import { useEffect, useMemo, useState } from "react";

import { Dialog } from "@/components/ui/Dialog";
import { api, type AssetListItem, type FolderItem } from "@/lib/api";

interface ComparePickerDialogProps {
  open: boolean;
  sourceAsset: AssetListItem | null;
  /** Pasta em que o seletor abre (vazio = raiz). */
  initialFolder?: string;
  onClose: () => void;
  onPick: (asset: AssetListItem) => void;
}

export function ComparePickerDialog({
  open,
  sourceAsset,
  initialFolder = "",
  onClose,
  onPick,
}: ComparePickerDialogProps) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<AssetListItem[]>([]);
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [currentFolder, setCurrentFolder] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Abre na pasta de quem pediu, e não na raiz: o arquivo B quase sempre mora
  // ao lado do A. Navegar dentro do seletor não volta para ela; só uma nova
  // abertura volta. Ajustado no render, e não num efeito, para o primeiro
  // quadro já ser o da pasta certa — e sem a lista da última abertura, que
  // ficaria na tela até a busca nova responder.
  const openedFor = open && sourceAsset ? `${sourceAsset.id}\n${initialFolder}` : null;
  const [shownFor, setShownFor] = useState<string | null>(null);
  if (openedFor !== shownFor) {
    setShownFor(openedFor);
    if (openedFor !== null) {
      setQuery("");
      setCurrentFolder(initialFolder.replace(/^\/+|\/+$/g, ""));
      setItems([]);
      setFolders([]);
      setLoading(true);
      setError(null);
    }
  }

  useEffect(() => {
    if (!open || !sourceAsset) return;
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const [foldersRes, assetsRes] = await Promise.all([
          api.listFolders(currentFolder),
          api.listAssets({
            folder: currentFolder,
            search: query.trim() || undefined,
            limit: 200,
          }),
        ]);
        if (cancelled) return;
        setFolders(foldersRes.subfolders);
        setItems(assetsRes.filter((item) => item.id !== sourceAsset.id));
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Falha ao buscar arquivos.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [currentFolder, open, query, sourceAsset]);

  /** Cada nível do caminho com a pasta para onde ele leva. */
  const breadcrumb = useMemo(() => {
    const parts = currentFolder ? currentFolder.split("/") : [];
    return [
      { label: "Raiz", path: "" },
      ...parts.map((part, index) => ({ label: part, path: parts.slice(0, index + 1).join("/") })),
    ];
  }, [currentFolder]);

  const parentFolder = useMemo(() => {
    if (!currentFolder) return "";
    const idx = currentFolder.lastIndexOf("/");
    return idx === -1 ? "" : currentFolder.slice(0, idx);
  }, [currentFolder]);

  const filtered = useMemo(() => items, [items]);

  return (
    <Dialog
      title={sourceAsset ? `Comparar "${sourceAsset.filename}" com...` : "Comparar arquivos"}
      open={open}
      onClose={onClose}
    >
      <div className="space-y-3">
        <nav
          aria-label="Pasta"
          className="flex flex-wrap items-center gap-x-1 rounded border border-ink-700 bg-ink-950/60 px-2 py-1.5 text-xs text-ink-400"
        >
          <span className="mr-1 text-ink-500">Pasta:</span>
          {breadcrumb.map((crumb, index) => {
            const isLast = index === breadcrumb.length - 1;
            return (
              <span key={crumb.path || "/"} className="flex min-w-0 items-center gap-x-1">
                {index > 0 ? <span className="text-ink-600">/</span> : null}
                {isLast ? (
                  <span className="truncate text-ink-200">{crumb.label}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery("");
                      setCurrentFolder(crumb.path);
                    }}
                    className="truncate text-ink-400 hover:text-emerald-300 hover:underline"
                  >
                    {crumb.label}
                  </button>
                )}
              </span>
            );
          })}
        </nav>

        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar arquivo nesta pasta"
          className="w-full rounded border border-ink-600 bg-ink-950 px-3 py-2 text-sm text-ink-100 outline-none ring-0 focus:border-emerald-500"
        />

        {loading ? <div className="text-xs text-ink-400">Buscando...</div> : null}
        {error ? <div className="text-xs text-red-300">{error}</div> : null}

        <div className="max-h-[420px] space-y-2 overflow-auto pr-1">
          {currentFolder ? (
            <button
              type="button"
              onClick={() => setCurrentFolder(parentFolder)}
              className="flex w-full items-center gap-2 rounded border border-ink-700 bg-ink-900/40 px-2 py-2 text-left text-xs text-ink-300 hover:border-emerald-600 hover:bg-ink-800"
            >
              <span className="text-ink-500">↩</span>
              Voltar para pasta anterior
            </button>
          ) : null}

          {folders.map((folder) => (
            <button
              key={`folder:${folder.path}`}
              type="button"
              onClick={() => {
                setQuery("");
                setCurrentFolder(folder.path);
              }}
              className="flex w-full items-center gap-3 rounded border border-ink-700 bg-ink-900/30 px-2 py-2 text-left hover:border-emerald-600 hover:bg-ink-800"
            >
              <span className="text-yellow-500">📁</span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-ink-100">{folder.name}</div>
                <div className="text-[11px] text-ink-500">{folder.asset_count} arquivos</div>
              </div>
              {/* A ordem já vem do servidor, com as favoritas na frente. */}
              {folder.favorite ? (
                <span className="text-amber-400" title="Pasta favorita" aria-label="Favorita">
                  ★
                </span>
              ) : null}
            </button>
          ))}

          {!loading && !error && filtered.length === 0 && folders.length === 0 ? (
            <div className="text-xs text-ink-500">Nenhum item encontrado nesta pasta.</div>
          ) : null}

          {filtered.map((asset) => (
            <button
              key={asset.id}
              type="button"
              onClick={() => onPick(asset)}
              className="flex w-full items-center gap-3 rounded border border-ink-700 bg-ink-800/50 px-2 py-2 text-left hover:border-emerald-600 hover:bg-ink-800"
            >
              <div className="flex h-12 w-10 shrink-0 items-center justify-center overflow-hidden rounded bg-ink-900">
                {asset.thumb_url ? (
                  <img
                    src={asset.thumb_url}
                    alt=""
                    className="h-full w-full object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <span className="text-[10px] text-ink-600">PDF</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-ink-100" title={asset.filename}>
                  {asset.filename}
                </div>
                <div className="truncate text-[11px] text-ink-400" title={asset.folder_path || "Raiz"}>
                  {asset.folder_path || "Raiz"}
                </div>
              </div>
              <span className="rounded bg-emerald-700 px-2 py-1 text-[11px] font-medium text-white">
                Comparar
              </span>
            </button>
          ))}
        </div>
      </div>
    </Dialog>
  );
}
